import React, { useEffect, useRef, useState } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { Button } from "./ui/button";
import { Upload, FileText, Image as ImgIcon, Trash2, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-scope attachment block — list + upload + delete + download.
 *
 * Scopes (must match backend SCOPES tuple): "distributor" | "daybook" | "sales-register".
 *
 * Props:
 *   scope       — string, required
 *   scopeId     — string, required (distributor id, or "<from>__<to>" for reports)
 *   canEdit     — boolean (gates the upload + delete buttons in addition to the backend gate)
 *   testIdPrefix — for stable test IDs
 */
export default function DocAttachments({ scope, scopeId, canEdit = false, testIdPrefix = "att" }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/attachments/${scope}/${encodeURIComponent(scopeId)}`);
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail || "Couldn't load attachments");
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, scopeId]);

    const pickFile = () => fileRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset so the same file can be re-picked
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File too large — keep it under 10 MB");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/attachments/${scope}/${encodeURIComponent(scopeId)}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Uploaded "${file.name}"`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (id) => {
        if (!confirm("Remove this attachment? It can't be undone from here.")) return;
        try {
            await api.delete(`/attachments/${id}`);
            await load();
            toast.success("Removed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't remove");
        }
    };

    return (
        <div data-testid={`${testIdPrefix}-block`}>
            {canEdit && (
                <div className="mb-3 flex items-center gap-2">
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
                        className="hidden"
                        onChange={handleFile}
                        data-testid={`${testIdPrefix}-input`}
                    />
                    <Button
                        onClick={pickFile}
                        disabled={uploading}
                        variant="outline"
                        size="sm"
                        className="rounded-none border-sky-300 text-sky-700"
                        data-testid={`${testIdPrefix}-upload`}
                    >
                        <Upload className="w-3.5 h-3.5 mr-1.5" />{uploading ? "Uploading…" : "Upload document"}
                    </Button>
                    <span className="text-[11px] text-stone-500">PDF, image, CSV or XLSX · max 10 MB</span>
                </div>
            )}

            {loading ? (
                <div className="text-xs text-stone-500" data-testid={`${testIdPrefix}-loading`}>Loading attachments…</div>
            ) : items.length === 0 ? (
                <div className="text-xs text-stone-500 italic" data-testid={`${testIdPrefix}-empty`}>
                    No supporting documents attached yet{canEdit ? " — upload one above." : "."}
                </div>
            ) : (
                <ul className="divide-y divide-stone-100 border border-stone-200" data-testid={`${testIdPrefix}-list`}>
                    {items.map((a) => (
                        <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`${testIdPrefix}-item-${a.id}`}>
                            <Icon mime={a.content_type} />
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-stone-900 truncate" title={a.filename}>{a.filename}</div>
                                <div className="text-[10px] text-stone-500">
                                    {a.uploaded_by_email || "—"} · {(a.size / 1024).toFixed(0)} KB · {(a.created_at || "").slice(0, 10)}
                                </div>
                            </div>
                            <a
                                href={`${BACKEND_URL}${a.url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sky-700 hover:underline text-xs inline-flex items-center"
                                data-testid={`${testIdPrefix}-open-${a.id}`}
                            >
                                <ExternalLink className="w-3.5 h-3.5 mr-1" />Open
                            </a>
                            <a
                                href={`${BACKEND_URL}${a.url}`}
                                download={a.filename}
                                className="text-stone-700 hover:underline text-xs inline-flex items-center"
                                data-testid={`${testIdPrefix}-download-${a.id}`}
                            >
                                <Download className="w-3.5 h-3.5 mr-1" />Download
                            </a>
                            {canEdit && (
                                <button
                                    onClick={() => remove(a.id)}
                                    className="text-rose-700 hover:text-rose-800"
                                    title="Remove"
                                    data-testid={`${testIdPrefix}-del-${a.id}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Icon({ mime }) {
    if ((mime || "").startsWith("image/")) return <ImgIcon className="w-4 h-4 text-stone-500 flex-shrink-0" />;
    return <FileText className="w-4 h-4 text-stone-500 flex-shrink-0" />;
}
